import type { ComponentChildren } from "preact";
import { useId } from "preact/hooks";
import { cx } from "../cx";

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SelectProps<T extends string> {
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  label?: ComponentChildren;
  description?: ComponentChildren;
  disabled?: boolean;
  class?: string;
  /** Accessible name when no visible `label` is given. */
  ariaLabel?: string;
}

/** Native select, styled. With `label` it renders as a settings row (label left, control right). */
export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  description,
  disabled = false,
  class: cls,
  ariaLabel,
}: SelectProps<T>) {
  const id = useId();
  const control = (
    <span class="select__box">
      <select
        id={id}
        class="select__input"
        value={value}
        disabled={disabled}
        aria-label={label ? undefined : ariaLabel}
        onChange={(e) => onChange(e.currentTarget.value as T)}
        data-nav=""
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </span>
  );
  if (!label) return <div class={cx("select", cls)}>{control}</div>;
  return (
    <div class={cx("select", "select--row", disabled && "is-disabled", cls)}>
      <label class="select__text" for={id}>
        <span class="select__label">{label}</span>
        {description && <span class="select__desc">{description}</span>}
      </label>
      {control}
    </div>
  );
}
